import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Plus, Trash2, UserPlus, Bell, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UserManagement = () => {
  const { user } = useOutletContext();
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'staff',
    linked_entity_type: null,
    linked_entity_id: null,
    phone: '',
    whatsapp_notifications: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, vendorsRes, customersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/users`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/vendors`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/customers`, { withCredentials: true })
      ]);
      setUsers(usersRes.data);
      setVendors(vendorsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.put(`${BACKEND_URL}/api/users/${editingUser.user_id}`, {
          name: formData.name,
          phone: formData.phone,
          whatsapp_notifications: formData.whatsapp_notifications,
          role: formData.role,
          linked_entity_type: formData.linked_entity_type,
          linked_entity_id: formData.linked_entity_id,
        }, { withCredentials: true });
        toast.success('User updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/users/invite`, formData, { withCredentials: true });
        toast.success(`User invited successfully! They can now login with ${formData.email}`);
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const startEdit = (u) => {
    setEditingUser(u);
    setFormData({
      email: u.email,
      name: u.name,
      role: u.role,
      linked_entity_type: u.linked_entity_type || null,
      linked_entity_id: u.linked_entity_id || null,
      phone: u.phone || '',
      whatsapp_notifications: u.whatsapp_notifications || false,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/users/${userId}`, {
        withCredentials: true
      });
      toast.success('User removed successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      name: '',
      role: 'staff',
      linked_entity_type: null,
      linked_entity_id: null,
      phone: '',
      whatsapp_notifications: false
    });
  };

  const handleRoleChange = (role) => {
    setFormData({
      ...formData,
      role,
      linked_entity_type: null,
      linked_entity_id: null
    });
  };

  const getRoleBadge = (role) => {
    const styles = {
      'admin': 'bg-purple-100 text-purple-800 border-purple-200',
      'staff': 'bg-blue-100 text-blue-800 border-blue-200',
      'vendor': 'bg-amber-100 text-amber-800 border-amber-200',
      'client': 'bg-lime-100 text-lime-800 border-lime-200'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${styles[role] || styles['staff']}`}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  const getLinkedEntityName = (user) => {
    if (!user.linked_entity_id) return '-';
    
    if (user.linked_entity_type === 'vendor') {
      const vendor = vendors.find(v => v.vendor_id === user.linked_entity_id);
      return vendor?.vendor_name || 'Unknown Vendor';
    } else if (user.linked_entity_type === 'customer') {
      const customer = customers.find(c => c.customer_id === user.linked_entity_id);
      return customer?.name || 'Unknown Customer';
    }
    return '-';
  };

  if (loading) {
    return <div className="text-stone-600">Loading users...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            User Management
          </h1>
          <p className="mt-2 text-base text-stone-600">Invite and manage users with different access levels</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="invite-user-btn" className="bg-amber-700 hover:bg-amber-800 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Invite User
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit User' : 'Invite New User'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  data-testid="user-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={!!editingUser}
                  placeholder="user@example.com"
                  className={`border-stone-200 focus:ring-amber-700 ${editingUser ? 'opacity-60' : ''}`}
                />
              </div>
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  data-testid="user-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Full Name"
                  className="border-stone-200 focus:ring-amber-700"
                />
              </div>
              <div>
                <Label htmlFor="role">Role *</Label>
                <Select value={formData.role} onValueChange={handleRoleChange} required>
                  <SelectTrigger data-testid="user-role-select" className="border-stone-200 focus:ring-amber-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin (full access)</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="vendor">Vendor</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.role === 'vendor' && (
                <div>
                  <Label htmlFor="vendor">Link to Vendor *</Label>
                  <Select
                    value={formData.linked_entity_id || ''}
                    onValueChange={(value) => setFormData({
                      ...formData,
                      linked_entity_type: 'vendor',
                      linked_entity_id: value
                    })}
                    required
                  >
                    <SelectTrigger data-testid="vendor-select" className="border-stone-200 focus:ring-amber-700">
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map((vendor) => (
                        <SelectItem key={vendor.vendor_id} value={vendor.vendor_id}>
                          {vendor.vendor_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.role === 'client' && (
                <div>
                  <Label htmlFor="customer">Link to Customer *</Label>
                  <Select
                    value={formData.linked_entity_id || ''}
                    onValueChange={(value) => setFormData({
                      ...formData,
                      linked_entity_type: 'customer',
                      linked_entity_id: value
                    })}
                    required
                  >
                    <SelectTrigger data-testid="customer-select" className="border-stone-200 focus:ring-amber-700">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.customer_id} value={customer.customer_id}>
                          {customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Phone + WhatsApp toggle */}
              <div>
                <Label className="text-xs text-stone-600 mb-1">Phone Number</Label>
                <Input data-testid="user-phone" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="+91..." className="border-stone-200 text-xs" />
              </div>
              <div className="flex items-center justify-between py-2 px-1 rounded-lg bg-green-50 border border-green-200">
                <div className="flex items-center gap-2">
                  <Bell className="w-3.5 h-3.5 text-green-700" />
                  <Label className="text-xs text-green-800 font-medium cursor-pointer">WhatsApp Notifications</Label>
                </div>
                <Switch data-testid="user-whatsapp-toggle" checked={formData.whatsapp_notifications} onCheckedChange={v => setFormData({ ...formData, whatsapp_notifications: v })} />
              </div>

              <Button type="submit" data-testid="submit-invite-btn" className="w-full bg-amber-700 hover:bg-amber-800 text-white">
                {editingUser ? <><Pencil className="w-4 h-4 mr-2" />Update User</> : <><UserPlus className="w-4 h-4 mr-2" />Send Invitation</>}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Name</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Email</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Role</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Linked To</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Phone</th>
                <th className="text-right px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200">
              {users.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-stone-500">
                    No users found. Invite users to get started.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="hover:bg-stone-50 transition-colors duration-200">
                    <td className="px-6 py-4 font-medium text-stone-900">{u.name}</td>
                    <td className="px-6 py-4 text-stone-700">{u.email}</td>
                    <td className="px-6 py-4">{getRoleBadge(u.role)}</td>
                    <td className="px-6 py-4 text-stone-700">{getLinkedEntityName(u)}</td>
                    <td className="px-6 py-4 text-stone-600 text-xs">
                      {u.phone || '-'}
                      {u.whatsapp_notifications && <Bell className="w-3 h-3 inline ml-1.5 text-green-600" />}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex gap-2 justify-end">
                        <button
                          data-testid={`edit-user-${u.user_id}`}
                          onClick={() => startEdit(u)}
                          className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 transition-colors duration-200"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {u.user_id !== user.user_id && (
                          <button
                            data-testid={`delete-user-${u.user_id}`}
                            onClick={() => handleDelete(u.user_id)}
                            className="inline-flex items-center gap-1 text-red-700 hover:text-red-800 transition-colors duration-200"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-stone-900 mb-2">User Role Permissions:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
          <div>
            <div className="font-semibold text-purple-800 mb-1">Admin</div>
            <div className="text-stone-600">Full access including profit tracking and user management</div>
          </div>
          <div>
            <div className="font-semibold text-blue-800 mb-1">Staff</div>
            <div className="text-stone-600">Manage orders and operations (no profit data)</div>
          </div>
          <div>
            <div className="font-semibold text-amber-800 mb-1">Vendor</div>
            <div className="text-stone-600">View and update delivery status for their products</div>
          </div>
          <div>
            <div className="font-semibold text-lime-800 mb-1">Client</div>
            <div className="text-stone-600">View their projects and order status (read-only)</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
